import type { ReactNode } from 'react';
import type { LoadBand } from '@rundown/domain';
import type { ClientStatus } from '@rundown/types';
import { cx } from '../../lib/cx';
import { STATUS_LABELS } from '../requests/labels';
import type { TimelineState } from './sprintMeta';

/**
 * The small pieces the Sprints and Plan screens share, so a sprint's state
 * chip, its load bar and a status word look the same on both — the two
 * screens are two views of one plan, and a reader moving between them should
 * not have to re-learn what a colour means.
 */

/** The prototype's 11px uppercase section label. */
export function SectionLabel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cx('fs-11 font-bold tracking-[0.06em] whitespace-nowrap text-muted uppercase', className)}
    >
      {children}
    </div>
  );
}

const STATE_CHIP: Readonly<Record<TimelineState, string>> = {
  released: 'bg-teal-tint text-teal',
  // Running is the loudest of the five on purpose: it is the only one that
  // answers "what is the team doing right now".
  started: 'bg-success text-white',
  locked: 'bg-ink text-white',
  planning: 'bg-accent-tint text-accent',
  projected: 'bg-surface-3 text-muted',
};

/** The small uppercase chip on a sprint card. */
export function StateChip({ state, children }: { state: TimelineState; children: ReactNode }) {
  return (
    <span
      className={cx(
        'rounded-sm px-1.5 py-0.5 fs-9 font-extrabold tracking-[0.08em] whitespace-nowrap uppercase',
        STATE_CHIP[state],
      )}
    >
      {children}
    </span>
  );
}

/** The same state as a rounded pill, beside a heading. */
export function StatePill({ state, children }: { state: TimelineState; children: ReactNode }) {
  return (
    <span className={cx('rounded-full px-2.5 py-0.5 fs-11 font-bold whitespace-nowrap', STATE_CHIP[state])}>
      {children}
    </span>
  );
}

export const LOAD_TEXT: Readonly<Record<LoadBand, string>> = {
  light: 'text-load-light',
  loaded: 'text-load-loaded',
  overloaded: 'text-load-overloaded',
};

export const LOAD_FILL: Readonly<Record<LoadBand, string>> = {
  light: 'bg-load-light',
  loaded: 'bg-load-loaded',
  overloaded: 'bg-load-overloaded',
};

/** A thin load bar. `band` null draws it in the delivered colour. */
export function Meter({
  width,
  band,
  className,
  thick = false,
}: {
  width: number;
  band: LoadBand | null;
  className?: string;
  thick?: boolean;
}) {
  return (
    <div
      role="meter"
      aria-valuenow={Math.round(width)}
      aria-valuemin={0}
      aria-valuemax={100}
      className={cx('w-full overflow-hidden rounded-full bg-surface-4', thick ? 'h-1.5' : 'h-1', className)}
    >
      <div
        className={cx(
          'h-full rounded-full transition-[width] duration-200',
          band === null ? 'bg-success' : LOAD_FILL[band],
        )}
        style={{ width: `${width}%` }}
      />
    </div>
  );
}

const STATUS_TEXT: Readonly<Record<ClientStatus, string>> = {
  received: 'text-muted',
  queued: 'text-muted',
  scheduled: 'text-violet-strong',
  in_progress: 'text-accent',
  testing: 'text-warn',
  released: 'text-success',
};

export function StatusText({ status, className }: { status: ClientStatus; className?: string }) {
  return (
    <span className={cx('fs-12 font-semibold whitespace-nowrap', STATUS_TEXT[status], className)}>
      {STATUS_LABELS[status]}
    </span>
  );
}
